/**
 * ManualLinesEditor — find ChannelMeter rows (month → payee/channel search)
 * and set new values for them, or remove them. Each line starts from
 * ChannelMeter's current values; the server re-measures them on save, so the
 * net change the apply dialog confirms is always the server's.
 */

import React, { useState } from 'react';
import { Button, Spinner, tableStyles } from '../../ui';
import { getCorrectableRows, EarningsBucket, MonthRow } from '../../../services/earningsCorrectionsApi';
import {
  BUCKET_LABEL,
  correctionErrorMessage,
  formatCount,
  formatSignedCents,
  formatUsd,
  isCorrectableChannelId,
  isMonth,
  ManualLineDraft,
  manualDeltaCents,
  manualDraftFromRow,
  monthLabel,
  parseManualLine,
} from './correctionModel';
import { cs, deltaColor, noticeStyle } from './correctionStyles';

interface ManualLinesEditorProps {
  drafts: ManualLineDraft[];
  onChange: (drafts: ManualLineDraft[]) => void;
  defaultMonth: string;
}

type BucketFilter = EarningsBucket | 'all';

const ManualLinesEditor: React.FC<ManualLinesEditorProps> = ({ drafts, onChange, defaultMonth }) => {
  const [month, setMonth] = useState(defaultMonth);
  const [bucket, setBucket] = useState<BucketFilter>('directSold');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<MonthRow[] | null>(null);
  const [meta, setMeta] = useState<{ total: number; truncated: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const added = new Set(drafts.map((d) => d.row.rowKeyId));

  const find = async () => {
    if (!isMonth(month)) {
      setError('Pick a month.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getCorrectableRows(month, { q: query.trim() || undefined, bucket: bucket === 'all' ? undefined : bucket });
      setRows(res.rows);
      setMeta({ total: res.total, truncated: res.truncated });
    } catch (err) {
      setRows(null);
      setMeta(null);
      setError(correctionErrorMessage(err, 'Failed to load ChannelMeter rows.'));
    } finally {
      setLoading(false);
    }
  };

  const update = (rowKeyId: string, patch: Partial<ManualLineDraft>) =>
    onChange(drafts.map((d) => (d.row.rowKeyId === rowKeyId ? { ...d, ...patch } : d)));

  return (
    <div style={cs.stack}>
      <div style={{ ...cs.section, padding: 16 }}>
        <div style={cs.subheading}>Find ChannelMeter rows</div>
        <div style={cs.row}>
          <input
            aria-label="Row month"
            type="month"
            style={{ ...cs.select, width: 160 }}
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
          <select aria-label="Bucket" style={cs.select} value={bucket} onChange={(e) => setBucket(e.target.value as BucketFilter)}>
            <option value="directSold">Direct Sold</option>
            <option value="adSense">AdSense</option>
            <option value="all">Both</option>
          </select>
          <input
            aria-label="Search rows"
            style={{ ...cs.input, width: 260 }}
            placeholder="Payee or channel name, or a UC… id"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') find();
            }}
          />
          <Button variant="subtle" size="sm" onClick={find} disabled={loading}>
            {loading ? 'Searching…' : 'Find rows'}
          </Button>
        </div>
        {error && (
          <div style={{ ...noticeStyle('danger'), marginTop: 8 }} role="alert">
            {error}
          </div>
        )}
        {loading && (
          <div style={cs.loadingRow}>
            <Spinner label="Loading rows…" />
          </div>
        )}
        {!loading && rows && (
          <div style={{ marginTop: 10 }}>
            <div style={cs.small}>
              {meta?.total ?? rows.length} row(s) in {monthLabel(month)}
              {meta?.truncated ? `, showing the first ${rows.length}. Narrow the search.` : ''}
            </div>
            {rows.length > 0 && (
              <div style={{ ...tableStyles.wrapScroll, maxHeight: 280, overflowY: 'auto', marginTop: 6 }}>
                <table style={tableStyles.table}>
                  <thead>
                    <tr>
                      <th style={tableStyles.thSticky}>Payee</th>
                      <th style={tableStyles.thSticky}>Channel</th>
                      <th style={tableStyles.thSticky}>Source</th>
                      <th style={{ ...tableStyles.thSticky, textAlign: 'right' }}>Earnings</th>
                      <th style={{ ...tableStyles.thSticky, textAlign: 'right' }}>Gross</th>
                      <th style={tableStyles.thSticky}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const correctable = isCorrectableChannelId(r.channelId);
                      return (
                        <tr key={r.rowKeyId}>
                          <td style={tableStyles.td}>{r.userName}</td>
                          <td style={tableStyles.td}>
                            <div>{r.channelName || '—'}</div>
                            <code style={cs.mono}>{r.channelId}</code>
                          </td>
                          <td style={tableStyles.td}>
                            {BUCKET_LABEL[r.bucket]} · {r.revenueSource}
                            {r.ownerCorrectionId && <div style={cs.small}>Corrected by {r.ownerCorrectionId}</div>}
                          </td>
                          <td style={tableStyles.tdNum}>{formatUsd(r.values.earningsUsd)}</td>
                          <td style={tableStyles.tdNum}>{formatUsd(r.values.grossUsd)}</td>
                          <td style={tableStyles.td}>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={added.has(r.rowKeyId) || !correctable}
                              title={correctable ? undefined : 'Only real YouTube channels (UC… ids) can be corrected'}
                              onClick={() => onChange([...drafts, manualDraftFromRow(r)])}
                            >
                              {added.has(r.rowKeyId) ? 'Added' : 'Add'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <div style={cs.subheading}>Lines ({drafts.length})</div>
        {drafts.length === 0 ? (
          <div style={cs.small}>Add a row above, then type its corrected values.</div>
        ) : (
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Row</th>
                  <th style={tableStyles.th}>Change</th>
                  <th style={tableStyles.thNum}>Gross</th>
                  <th style={tableStyles.thNum}>Earnings</th>
                  <th style={tableStyles.thNum}>Performance</th>
                  <th style={tableStyles.thNum}>Δ earnings</th>
                  <th style={tableStyles.th}>Note</th>
                  <th style={tableStyles.th}></th>
                </tr>
              </thead>
              <tbody>
                {drafts.map((d) => {
                  const result = parseManualLine(d);
                  const delta = manualDeltaCents(d);
                  const setting = d.op === 'set';
                  return (
                    <tr key={d.row.rowKeyId}>
                      <td style={tableStyles.td}>
                        <div style={{ fontWeight: 500 }}>{d.row.channelName}</div>
                        <div style={cs.small}>
                          {monthLabel(d.row.month)} · {d.row.userName} · {BUCKET_LABEL[d.row.bucket]} · {d.row.revenueSource}
                        </div>
                        {result.error && <div style={{ ...cs.small, color: 'var(--danger)' }}>{result.error}</div>}
                      </td>
                      <td style={tableStyles.td}>
                        <select
                          aria-label={`Change for ${d.row.channelName}`}
                          style={cs.select}
                          value={d.op}
                          onChange={(e) => update(d.row.rowKeyId, { op: e.target.value as 'set' | 'remove' })}
                        >
                          <option value="set">Set to</option>
                          <option value="remove">Remove</option>
                        </select>
                      </td>
                      <td style={tableStyles.tdNum}>
                        {setting && (
                          <input
                            aria-label={`Gross for ${d.row.channelName}`}
                            style={cs.numInput}
                            inputMode="decimal"
                            value={d.grossUsd}
                            onChange={(e) => update(d.row.rowKeyId, { grossUsd: e.target.value })}
                          />
                        )}
                        <div style={cs.small}>CM {formatUsd(d.row.values.grossUsd)}</div>
                      </td>
                      <td style={tableStyles.tdNum}>
                        {setting && (
                          <input
                            aria-label={`Earnings for ${d.row.channelName}`}
                            style={cs.numInput}
                            inputMode="decimal"
                            value={d.earningsUsd}
                            onChange={(e) => update(d.row.rowKeyId, { earningsUsd: e.target.value })}
                          />
                        )}
                        <div style={cs.small}>CM {formatUsd(d.row.values.earningsUsd)}</div>
                      </td>
                      <td style={tableStyles.tdNum}>
                        {setting && (
                          <input
                            aria-label={`Performance for ${d.row.channelName}`}
                            style={cs.numInput}
                            inputMode="numeric"
                            value={d.performance}
                            onChange={(e) => update(d.row.rowKeyId, { performance: e.target.value })}
                          />
                        )}
                        <div style={cs.small}>
                          CM {formatCount(d.row.values.performance)} {d.row.values.performanceMetric ?? ''}
                        </div>
                      </td>
                      <td style={{ ...tableStyles.tdNum, fontWeight: 600, color: delta == null ? 'var(--fg-3)' : deltaColor(delta) }}>
                        {delta == null ? '—' : formatSignedCents(delta)}
                      </td>
                      <td style={tableStyles.td}>
                        <input
                          aria-label={`Note for ${d.row.channelName}`}
                          style={{ ...cs.input, width: 180 }}
                          maxLength={500}
                          value={d.note}
                          onChange={(e) => update(d.row.rowKeyId, { note: e.target.value })}
                        />
                      </td>
                      <td style={tableStyles.td}>
                        <Button
                          variant="subtle"
                          size="sm"
                          onClick={() => onChange(drafts.filter((x) => x.row.rowKeyId !== d.row.rowKeyId))}
                        >
                          Drop
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualLinesEditor;
